import { Link, router } from "@inertiajs/react";
import { useEffect, useRef, useState } from "react";
import { cn } from "../../lib/utils";

function BrandLogo({ className }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden>
      <defs>
        <linearGradient id="public-brand-logo-gradient" x1="8" y1="6" x2="56" y2="58" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="#22d3ee" />
          <stop offset="0.5" stopColor="#38bdf8" />
          <stop offset="1" stopColor="#34d399" />
        </linearGradient>
      </defs>
      <rect x="4" y="4" width="56" height="56" rx="16" fill="#020617" stroke="url(#public-brand-logo-gradient)" strokeWidth="3" />
      <path
        d="M17 45L30.5 17.5a2 2 0 013 0L47 45a2 2 0 01-1.8 2.9H18.8A2 2 0 0117 45z"
        fill="none"
        stroke="url(#public-brand-logo-gradient)"
        strokeWidth="3.2"
        strokeLinejoin="round"
      />
      <circle cx="32" cy="33" r="5.5" fill="url(#public-brand-logo-gradient)" />
    </svg>
  );
}

function NavLink({ href, children }) {
  return (
    <a href={href} className="rounded-lg px-3 py-2 text-sm font-medium text-slate-200 transition hover:bg-slate-900">
      {children}
    </a>
  );
}

function PublicUserMenu({ auth }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);
  const displayName = auth?.name?.trim() || "Account";
  const canManageSettings = Boolean(auth?.superadmin);
  const items = [
    { route: "/app", label: "Generation" },
    { route: "/app/account", label: "Account" },
    { route: "/app/billing", label: "Billing" },
    ...(canManageSettings ? [{ route: "/app/settings", label: "Settings" }] : [])
  ];
  const dropdownItemBaseClass =
    "flex w-full cursor-pointer items-center justify-between rounded-xl px-3 py-2 text-sm font-medium transition-all duration-150 focus-visible:outline-none";
  const dropdownItemActiveClass =
    "bg-[linear-gradient(135deg,rgba(34,211,238,.2),rgba(59,130,246,.14))] text-cyan-100 shadow-[inset_0_0_0_1px_rgba(34,211,238,.08),0_10px_18px_rgba(2,6,23,.16)]";
  const dropdownItemIdleClass =
    "text-slate-200 hover:translate-x-0.5 hover:bg-slate-800 hover:text-white hover:shadow-[0_10px_20px_rgba(2,6,23,.26)] focus-visible:bg-slate-800 focus-visible:text-white";
  const dropdownSuperadminClass =
    "text-violet-100 hover:translate-x-0.5 hover:bg-violet-500/18 hover:text-white hover:shadow-[0_10px_20px_rgba(76,29,149,.18)] focus-visible:bg-violet-500/18 focus-visible:text-white";
  const dropdownSignOutClass =
    "text-rose-100 hover:translate-x-0.5 hover:bg-rose-500/18 hover:text-white hover:shadow-[0_10px_20px_rgba(190,24,93,.16)] focus-visible:bg-rose-500/18 focus-visible:text-white";

  useEffect(() => {
    if (!menuOpen) return undefined;

    const handleOutside = (event) => {
      const target = event.target;
      if (menuRef.current?.contains(target)) return;
      if (menuButtonRef.current?.contains(target)) return;
      setMenuOpen(false);
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };

    window.addEventListener("mousedown", handleOutside);
    window.addEventListener("keydown", handleEscape);

    return () => {
      window.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("keydown", handleEscape);
    };
  }, [menuOpen]);

  return (
    <div className="relative shrink-0">
      <button
        ref={menuButtonRef}
        type="button"
        aria-expanded={menuOpen}
        className="inline-flex items-center gap-2 rounded-full border border-cyan-400/35 bg-slate-950/70 px-1.5 py-1.5 shadow-[0_0_0_1px_rgba(8,145,178,.12),0_10px_24px_rgba(2,6,23,.28)] transition hover:border-cyan-300/55 hover:bg-slate-900/80"
        onClick={() => setMenuOpen((prev) => !prev)}
      >
        <img src={auth.avatar_url} alt={displayName} className="h-8 w-8 rounded-full object-cover" />
        <svg
          viewBox="0 0 20 20"
          className={cn("h-4 w-4 text-slate-300 transition-transform", menuOpen && "rotate-180")}
          aria-hidden
        >
          <path d="m5 7.5 5 5 5-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>

      {menuOpen ? (
        <div
          ref={menuRef}
          className="absolute right-0 top-full z-50 mt-2 w-[250px] overflow-hidden rounded-2xl border border-slate-700 bg-slate-950/95 p-2 shadow-[0_18px_40px_rgba(2,6,23,.55)] backdrop-blur"
        >
          <div className="rounded-xl border border-slate-800 bg-slate-900/70 px-3 py-2.5">
            <p className="truncate text-sm font-semibold text-slate-100">{displayName}</p>
            <p className="mt-0.5 truncate text-xs text-slate-400">{auth.email}</p>
          </div>

          <div className="mt-2 space-y-1">
            {items.map((item) => (
              <button
                key={`public-menu-${item.route}`}
                type="button"
                className={cn(
                  dropdownItemBaseClass,
                  item.route === "/app" ? dropdownItemActiveClass : dropdownItemIdleClass
                )}
                onClick={() => {
                  setMenuOpen(false);
                  router.visit(item.route);
                }}
              >
                <span>{item.label}</span>
              </button>
            ))}

            {canManageSettings ? (
              <button
                type="button"
                className={cn(dropdownItemBaseClass, dropdownSuperadminClass)}
                onClick={() => {
                  setMenuOpen(false);
                  router.visit("/admin/settings");
                }}
              >
                <span>Superadmin</span>
              </button>
            ) : null}

            <div className="my-1 h-px bg-slate-800" />

            <button
              type="button"
              className={cn(dropdownItemBaseClass, "font-normal", dropdownSignOutClass)}
              onClick={() => {
                setMenuOpen(false);
                router.delete("/users/sign_out");
              }}
            >
              <span>Sign out</span>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export function PublicNavbar({ auth }) {
  return (
    <header className="sticky top-0 z-40 border-b border-slate-800/90 bg-black/80 backdrop-blur-xl">
      <div className="mx-auto flex w-full max-w-[1140px] items-center gap-4 px-4 py-3 md:px-6">
        <Link href="/" className="flex items-center gap-2">
          <BrandLogo className="h-8 w-8 shrink-0" />
          <span className="text-sm font-semibold uppercase tracking-[0.18em] text-white">UGC Persona</span>
        </Link>

        <nav className="ml-auto flex items-center gap-2">
          <NavLink href="/docs">Docs</NavLink>
          <NavLink href="/#pricing">Pricing</NavLink>
          {auth?.id ? (
            <>
              <Link
                href="/app"
                className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:border-slate-500 hover:bg-slate-800"
              >
                Open app
              </Link>
              <PublicUserMenu auth={auth} />
            </>
          ) : (
            <a
              href="/users/sign_in"
              className="rounded-xl border border-slate-700 bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:border-slate-500 hover:bg-slate-800"
            >
              Log in
            </a>
          )}
        </nav>
      </div>
    </header>
  );
}
